import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/modules/auth/context/AuthContext";
import { publicService } from "../services/publicService";
import {
  Category,
  Subcategory,
  Product,
  FpoMarketStock,
  GroupedFpo,
  ProductEnquiryPayload,
  VendorStorefront,
} from "../types/public.types";
import { deduplicateProducts, groupFpoStocks } from "../utils/publicUtils";

/**
 * Custom hook to manage state and actions for the Category Catalog Page
 */
interface InitialCategoryData {
  category: Category;
  subcategories: Subcategory[];
  products: Product[];
  totalProducts: number;
}

export function useCategoryPage(categorySlug: string, initialData?: InitialCategoryData) {
  const [category, setCategory] = useState<Category | null>(initialData?.category || null);
  const [subcategories, setSubcategories] = useState<Subcategory[]>(initialData?.subcategories || []);
  const [products, setProducts] = useState<Product[]>(initialData?.products || []);
  const [loading, setLoading] = useState(!initialData);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(initialData ? (initialData.products.length < initialData.totalProducts) : true);
  const [hasProducts, setHasProducts] = useState(initialData ? (initialData.totalProducts > 0) : false);

  const lastSearchRef = useRef("");

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const loadCategoryData = async () => {
    if (!categorySlug) return;
    try {
      setLoading(true);
      setError(null);
      setPage(1);
      lastSearchRef.current = "";

      // 1. Fetch category by slug
      const catData = await publicService.getCategoryBySlug(categorySlug);
      if (!catData) throw new Error("Category not found");
      setCategory(catData);

      // 2. Fetch subcategories & products/services in parallel using category ID
      const [subcatData, prodData] = await Promise.all([
        publicService.getSubcategoriesByCategoryId(catData._id),
        categorySlug === "agri-services"
          ? publicService.getServices({ category: catData._id, page: 1, limit: 15 }).then(res => ({
              products: res.services,
              total: res.total
            }))
          : publicService.getProducts({ category: catData._id, page: 1, limit: 15 }),
      ]);

      setSubcategories(subcatData);
      setProducts(prodData.products || []);
      setHasProducts((prodData.total || 0) > 0);
      setHasMore((prodData.products || []).length < (prodData.total || 0));
    } catch (err: any) {
      setError(err.message || "An error occurred while loading data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData || initialData.category.slug !== categorySlug) {
      setSearchQuery("");
      setDebouncedSearch("");
      loadCategoryData();
    }
  }, [categorySlug, initialData]);

  // Handle debounced search query changes
  useEffect(() => {
    if (!category) return;
    if (debouncedSearch === lastSearchRef.current) return;

    const fetchFilteredProducts = async () => {
      try {
        setLoadingMore(true);
        setPage(1);
        lastSearchRef.current = debouncedSearch;
        const prodData = categorySlug === "agri-services"
          ? await publicService.getServices({
              category: category._id,
              page: 1,
              limit: 15,
              search: debouncedSearch,
            }).then(res => ({
              products: res.services,
              total: res.total
            }))
          : await publicService.getProducts({
              category: category._id,
              page: 1,
              limit: 15,
              search: debouncedSearch,
            });
        setProducts(prodData.products || []);
        setHasMore((prodData.products || []).length < (prodData.total || 0));
      } catch (err: any) {
        console.error("Error searching products:", err);
      } finally {
        setLoadingMore(false);
      }
    };

    fetchFilteredProducts();
  }, [debouncedSearch, category]);

  const loadMoreProducts = async () => {
    if (loading || loadingMore || !hasMore || !category) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const prodData = categorySlug === "agri-services"
        ? await publicService.getServices({
            category: category._id,
            page: nextPage,
            limit: 15,
            search: debouncedSearch,
          }).then(res => ({
            products: res.services,
            total: res.total
          }))
        : await publicService.getProducts({
            category: category._id,
            page: nextPage,
            limit: 15,
            search: debouncedSearch,
          });

      const newProducts = prodData.products || [];
      setProducts((prev) => {
        const nextProducts = [...prev, ...newProducts];
        setHasMore(nextProducts.length < (prodData.total || 0));
        return nextProducts;
      });
      setPage(nextPage);
    } catch (err: any) {
      console.error("Error loading more products:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    category,
    subcategories,
    products,
    filteredProducts: products,
    loading,
    loadingMore,
    error,
    searchQuery,
    setSearchQuery,
    hasMore,
    hasProducts,
    loadMoreProducts,
    reload: loadCategoryData,
  };
}

/**
 * Custom hook to manage state and actions for the Subcategory Page
 */
export function useSubcategoryPage(categorySlug: string, subcatSlug: string) {
  const [category, setCategory] = useState<Category | null>(null);
  const [subcategory, setSubcategory] = useState<Subcategory | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const loadSubcategoryData = async () => {
    if (!categorySlug || !subcatSlug) return;
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch category and subcategory in parallel
      const [catData, subcatData] = await Promise.all([
        publicService.getCategoryBySlug(categorySlug),
        publicService.getSubcategoryBySlug(subcatSlug),
      ]);

      if (!catData) throw new Error("Category not found");
      if (!subcatData) throw new Error("Subcategory not found");

      setCategory(catData);
      setSubcategory(subcatData);

      // 2. Fetch products/services under subcategory ID
      const prodData = categorySlug === "agri-services"
        ? await publicService.getServices({ subcategory: subcatData._id }).then(res => ({
            products: res.services
          }))
        : await publicService.getProducts({ subcategory: subcatData._id });

      setProducts(prodData.products || []);
    } catch (err: any) {
      setError(err.message || "An error occurred while loading catalog data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubcategoryData();
  }, [categorySlug, subcatSlug]);

  const filteredProducts = products.filter((prod) => {
    const stateName = typeof prod.location?.state === 'object' ? prod.location.state?.name : prod.location?.state;
    const distName = typeof prod.location?.district === 'object' ? prod.location.district?.name : prod.location?.district || prod.location?.city;
    const talukName = typeof prod.location?.taluk === 'object' ? prod.location.taluk?.name : prod.location?.taluk;

    const matchesSearch =
      (prod.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (prod.description || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (distName && distName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (stateName && stateName.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (talukName && talukName.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  return {
    category,
    subcategory,
    products,
    filteredProducts,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    reload: loadSubcategoryData,
  };
}

/**
 * Custom hook to manage state and actions for Product Details & Enquiry Form
 */
export function useProductDetailPage(slug: string) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerLocation, setBuyerLocation] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Variant and UI State
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number>(0);
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState<boolean>(false);

  const { user } = useAuth();

  // Prefill form states when user is logged in and modal opens
  useEffect(() => {
    if (isEnquiryModalOpen && user) {
      setBuyerName(user.name || "");
      setBuyerPhone(user.phoneNumber || "");
      setBuyerEmail(user.email || "");
      const locationParts = [user.district, user.state].filter(Boolean);
      setBuyerLocation(
        locationParts.join(", ") ||
        user.companyAddress ||
        user.address ||
        ""
      );
    }
  }, [isEnquiryModalOpen, user, setBuyerName, setBuyerPhone, setBuyerEmail, setBuyerLocation]);

  const fetchProduct = async () => {
    if (!slug) return;
    try {
      setLoading(true);
      setError(null);
      const data = await publicService.getProductBySlug(slug);
      if (!data) throw new Error("Product not found");
      setProduct(data);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [slug]);

  useEffect(() => {
    if (product) {
      setMessage(
        `Hi, I am interested in your product "${product.title}". Please contact me with more details and price quotes.`
      );
    }
  }, [product]);

  const handleEnquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!product) return;

    if (!buyerName.trim() || !buyerPhone.trim() || !message.trim()) {
      setSubmitError("Please fill in all required fields (Name, Phone number, and Message)");
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const payload: ProductEnquiryPayload = {
        productId: product._id,
        buyerName,
        buyerPhone,
        buyerEmail: buyerEmail || undefined,
        buyerLocation: buyerLocation || undefined,
        message,
      };

      await publicService.submitProductEnquiry(payload);
      setSubmitSuccess(true);
      setBuyerName("");
      setBuyerPhone("");
      setBuyerEmail("");
      setBuyerLocation("");
    } catch (err: any) {
      setSubmitError(err.message || "Something went wrong while submitting. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    product,
    loading,
    error,
    buyerName,
    setBuyerName,
    buyerPhone,
    setBuyerPhone,
    buyerEmail,
    setBuyerEmail,
    buyerLocation,
    setBuyerLocation,
    message,
    setMessage,
    submitting,
    submitSuccess,
    setSubmitSuccess,
    submitError,
    selectedVariantIndex,
    setSelectedVariantIndex,
    isEnquiryModalOpen,
    setIsEnquiryModalOpen,
    handleEnquirySubmit,
    reload: fetchProduct,
  };
}

/**
 * Custom hook to manage Service Details & Enquiry Form
 */
export function useServiceDetailPage(slug: string) {
  const [service, setService] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [buyerName, setBuyerName] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerLocation, setBuyerLocation] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Variant and UI State
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number>(0);
  const [isEnquiryModalOpen, setIsEnquiryModalOpen] = useState<boolean>(false);

  const { user } = useAuth();

  // Prefill form states when user is logged in and modal opens
  useEffect(() => {
    if (isEnquiryModalOpen && user) {
      setBuyerName(user.name || "");
      setBuyerPhone(user.phoneNumber || "");
      setBuyerEmail(user.email || "");
      const locationParts = [user.district, user.state].filter(Boolean);
      setBuyerLocation(
        locationParts.join(", ") ||
        user.companyAddress ||
        user.address ||
        ""
      );
    }
  }, [isEnquiryModalOpen, user, setBuyerName, setBuyerPhone, setBuyerEmail, setBuyerLocation]);

  const fetchService = async () => {
    if (!slug) return;
    try {
      setLoading(true);
      setError(null);
      const data = await publicService.getServiceBySlug(slug);
      if (!data) throw new Error("Service not found");
      setService(data);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchService();
  }, [slug]);

  useEffect(() => {
    if (service) {
      setMessage(
        `Hi, I am interested in your service "${service.title}". Please contact me with more details and price quotes.`
      );
    }
  }, [service]);

  const handleEnquirySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!service) return;

    if (!buyerName.trim() || !buyerPhone.trim() || !message.trim()) {
      setSubmitError("Please fill in all required fields (Name, Phone number, and Message)");
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError(null);

      const payload = {
        serviceId: service._id,
        vendorEmail: service.vendorDetails?.email,
        serviceTitle: service.title,
        buyerName,
        buyerPhone,
        buyerEmail: buyerEmail || undefined,
        buyerLocation: buyerLocation || undefined,
        message,
      };

      await publicService.submitServiceEnquiry(payload);
      setSubmitSuccess(true);
      setBuyerName("");
      setBuyerPhone("");
      setBuyerEmail("");
      setBuyerLocation("");
    } catch (err: any) {
      setSubmitError(err.message || "Something went wrong while submitting. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    service,
    loading,
    error,
    buyerName,
    setBuyerName,
    buyerPhone,
    setBuyerPhone,
    buyerEmail,
    setBuyerEmail,
    buyerLocation,
    setBuyerLocation,
    message,
    setMessage,
    submitting,
    submitSuccess,
    setSubmitSuccess,
    submitError,
    selectedVariantIndex,
    setSelectedVariantIndex,
    isEnquiryModalOpen,
    setIsEnquiryModalOpen,
    handleEnquirySubmit,
    reload: fetchService,
  };
}

/**
 * Custom hook to manage FPO directory connect listings
 */
export function useFpoConnect() {
  const [stocks, setStocks] = useState<FpoMarketStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters State
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  const fetchApprovedStocks = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await publicService.getFpoMarketStocks();
      setStocks(data);
    } catch (err: any) {
      setError(err.message || "An error occurred while loading FPOs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovedStocks();
  }, []);

  const uniqueDistricts = Array.from(
    new Set(
      stocks
        .map((stock) => {
          const dist = stock.fpoUser?.district || stock.district;
          return typeof dist === "object" ? dist?.name : dist;
        })
        .filter(Boolean)
    )
  ).sort();

  const uniqueCategories = Array.from(
    new Set(stocks.map((stock) => stock.category?.name).filter(Boolean))
  ).sort();

  const filteredStocks = stocks.filter((stock) => {
    const stockDistrict = stock.fpoUser?.district || stock.district;
    const stockDistrictName = typeof stockDistrict === "object" ? stockDistrict?.name : stockDistrict;
    const matchesDistrict = selectedDistrict ? stockDistrictName === selectedDistrict : true;
    const matchesCategory = selectedCategory ? stock.category?.name === selectedCategory : true;
    return matchesDistrict && matchesCategory;
  });

  const groupedFpos: GroupedFpo[] = groupFpoStocks(filteredStocks);

  const totalUniqueFposCount = Array.from(
    new Set(
      stocks
        .map((stock) => {
          const dist = stock.district;
          const distName = typeof dist === "object" ? dist?.name : dist;
          return stock.fpoUser && typeof stock.fpoUser === "object"
            ? (stock.fpoUser as any)._id
            : stock.fpoUser || `${stock.fpoName}-${distName}`;
        })
        .filter(Boolean)
    )
  ).length;

  return {
    stocks,
    loading,
    error,
    selectedDistrict,
    setSelectedDistrict,
    selectedCategory,
    setSelectedCategory,
    filteredStocks,
    groupedFpos,
    uniqueDistricts,
    uniqueCategories,
    totalUniqueFposCount,
    reload: fetchApprovedStocks,
  };
}

/**
 * Custom hook to manage FPO showroom detail page state
 */
export function useFpoDetailPage(id: string) {
  const [stocks, setStocks] = useState<FpoMarketStock[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("");

  const fetchStockDetails = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      let resolvedId = id;
      const isMongoId = /^[0-9a-fA-F]{24}$/.test(id);
      if (!isMongoId) {
        try {
          const profile = await publicService.getVendorProfileBySlug(id);
          resolvedId = profile._id;
        } catch (e) {
          console.error("Failed to resolve FPO slug", e);
        }
      }

      // 1. Try to fetch stocks by user ID
      let userStocks: FpoMarketStock[] = [];
      try {
        userStocks = await publicService.getFpoMarketStocksByUserId(resolvedId);
      } catch (e) {
        // Fallback handled below
      }

      // 2. If no stocks found, try fetching as stock ID
      if (userStocks.length === 0) {
        const singleStock = await publicService.getFpoMarketStockById(resolvedId);
        const userId =
          singleStock.fpoUser && typeof singleStock.fpoUser === "object"
            ? (singleStock.fpoUser as any)._id
            : singleStock.fpoUser;

        if (userId) {
          try {
            userStocks = await publicService.getFpoMarketStocksByUserId(userId);
          } catch (e) {
            userStocks = [singleStock];
          }
        } else {
          userStocks = [singleStock];
        }
      }

      if (userStocks.length === 0) {
        throw new Error("No approved market listings found for this FPO.");
      }

      setStocks(userStocks);
    } catch (err: any) {
      setError(err.message || "An error occurred while fetching details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStockDetails();
  }, [id]);

  const filteredStocks = stocks.filter((stockEntry) => {
    return selectedCategory ? stockEntry.category?.name === selectedCategory : true;
  });

  const uniqueCategories = Array.from(
    new Set(stocks.map((s) => s.category?.name).filter(Boolean))
  ).sort();

  return {
    stocks,
    loading,
    error,
    selectedCategory,
    setSelectedCategory,
    filteredStocks,
    uniqueCategories,
    reload: fetchStockDetails,
  };
}

/**
 * Custom hook to manage state and actions for the Vendor Storefront Page
 */
export function useVendorStorefrontPage(vendorId: string) {
  const [vendor, setVendor] = useState<VendorStorefront | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [hasProducts, setHasProducts] = useState(false);

  const lastSearchRef = useRef("");

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const loadVendorData = async () => {
    if (!vendorId) return;
    try {
      setLoading(true);
      setError(null);
      setPage(1);
      lastSearchRef.current = "";

      // 1. Fetch vendor profile (supports lookup by id, email, or slug)
      const isEmail = vendorId.includes("@") || vendorId.includes("%40");
      const isMongoId = /^[0-9a-fA-F]{24}$/.test(vendorId);

      let vendorData;
      if (isEmail) {
        vendorData = await publicService.getVendorProfileByEmail(decodeURIComponent(vendorId));
      } else if (isMongoId) {
        vendorData = await publicService.getVendorProfile(vendorId);
      } else {
        vendorData = await publicService.getVendorProfileBySlug(vendorId);
      }
      setVendor(vendorData);

      // 2. Fetch products using vendor ID
      if (vendorData._id) {
        const prodData = await publicService.getProducts({
          vendorId: vendorData._id,
          page: 1,
          limit: 15,
        });
        setProducts(prodData.products || []);
        setHasProducts((prodData.total || 0) > 0);
        setHasMore((prodData.products || []).length < (prodData.total || 0));
      } else {
        setProducts([]);
        setHasProducts(false);
        setHasMore(false);
      }
    } catch (err: any) {
      setError(err.message || "An error occurred while loading vendor data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearchQuery("");
    setDebouncedSearch("");
    loadVendorData();
  }, [vendorId]);

  // Handle debounced search query changes
  useEffect(() => {
    if (!vendor || !vendor._id) return;
    if (debouncedSearch === lastSearchRef.current) return;

    const fetchFilteredProducts = async () => {
      try {
        setLoadingMore(true);
        setPage(1);
        lastSearchRef.current = debouncedSearch;
        const prodData = await publicService.getProducts({
          vendorId: vendor._id,
          page: 1,
          limit: 15,
          search: debouncedSearch,
        });
        setProducts(prodData.products || []);
        setHasMore((prodData.products || []).length < (prodData.total || 0));
      } catch (err: any) {
        console.error("Error searching vendor products:", err);
      } finally {
        setLoadingMore(false);
      }
    };

    fetchFilteredProducts();
  }, [debouncedSearch, vendor]);

  const loadMoreProducts = async () => {
    if (loading || loadingMore || !hasMore || !vendor || !vendor._id) return;

    try {
      setLoadingMore(true);
      const nextPage = page + 1;
      const prodData = await publicService.getProducts({
        vendorId: vendor._id,
        page: nextPage,
        limit: 15,
        search: debouncedSearch,
      });

      const newProducts = prodData.products || [];
      setProducts((prev) => {
        const nextProducts = [...prev, ...newProducts];
        setHasMore(nextProducts.length < (prodData.total || 0));
        return nextProducts;
      });
      setPage(nextPage);
    } catch (err: any) {
      console.error("Error loading more vendor products:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    vendor,
    products,
    filteredProducts: products,
    loading,
    loadingMore,
    error,
    searchQuery,
    setSearchQuery,
    hasMore,
    hasProducts,
    loadMoreProducts,
    reload: loadVendorData,
  };
}

/**
 * Custom hook to manage state and actions for the Vendors Store List page
 */
export function useVendorsStoreList(initialVendors?: VendorStorefront[]) {
  const [vendors, setVendors] = useState<VendorStorefront[]>(initialVendors || []);
  const [loading, setLoading] = useState(!initialVendors);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");

  const fetchVendors = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await publicService.getVendors();
      setVendors(data);
    } catch (err: any) {
      setError(err.message || "An error occurred while loading vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialVendors || initialVendors.length === 0) {
      fetchVendors();
    }
  }, [initialVendors]);

  const uniqueDistricts = Array.from(
    new Set(vendors.map((v) => v.district).filter(Boolean))
  ).sort() as string[];

  const filteredVendors = vendors.filter((v) => {
    const storeName = v.storeName || v.name || "";
    const description = v.storeDescription || "";
    const district = v.district || "";
    const taluk = v.taluk || "";

    const matchesSearch =
      storeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      district.toLowerCase().includes(searchQuery.toLowerCase()) ||
      taluk.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesDistrict = selectedDistrict ? district === selectedDistrict : true;

    return matchesSearch && matchesDistrict;
  });

  return {
    vendors,
    filteredVendors,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    selectedDistrict,
    setSelectedDistrict,
    uniqueDistricts,
    reload: fetchVendors,
  };
}
